import React, { useState, useEffect } from 'react';
import { Send, Mail, MessageSquare, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axios';

const Contact = () => {
  const user = useAuthStore(state => state.user);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    customer_email: user?.email || '',
    customer_name: user ? `${user.first_name} ${user.last_name}` : ''
  });
  const [sending, setSending] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    axiosInstance.get('/faq').then((res) => {
      setFaqs(res.data?.faqs || []);
    }).catch(() => { /* fallback bleibt leer, hardcodierte Fallbacks siehe unten */ });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    if (!user && !formData.customer_email.trim()) {
      toast.error('Bitte E-Mail-Adresse eingeben');
      return;
    }

    setSending(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/messages/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user && { Authorization: `Bearer ${localStorage.getItem('token')}` })
          },
          body: JSON.stringify(formData)
        }
      );

      if (response.ok) {
        toast.success('Nachricht erfolgreich gesendet!');
        setFormData({
          subject: '',
          message: '',
          customer_email: user?.email || '',
          customer_name: user ? `${user.first_name} ${user.last_name}` : ''
        });
      } else {
        toast.error('Fehler beim Senden der Nachricht');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Fehler beim Senden der Nachricht');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MessageSquare className="h-10 w-10 text-[#D4AF37]" />
              <h1 
                className="text-4xl font-bold"
                data-editable="contact-page-title"
              >
                Kontakt
              </h1>
            </div>
            <p 
              className="text-lg text-gray-600"
              data-editable="contact-page-subtitle"
            >
              Haben Sie Fragen oder Anliegen? Schreiben Sie uns eine Nachricht!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle data-editable="contact-form-title">Nachricht senden</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {!user && (
                    <>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Name</label>
                        <Input
                          value={formData.customer_name}
                          onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                          placeholder="Ihr Name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">E-Mail *</label>
                        <Input
                          type="email"
                          value={formData.customer_email}
                          onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                          placeholder="ihre@email.de"
                          required
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="text-sm font-medium mb-1 block">Betreff *</label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Worum geht es?"
                      required
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">Nachricht *</label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Ihre Nachricht..."
                      rows={6}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#D4AF37] hover:bg-[#C9A961]"
                    disabled={sending}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {sending ? 'Wird gesendet...' : 'Nachricht senden'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Info */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Kontakt-Informationen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-gray-600">
                    Wir antworten in der Regel innerhalb von 24 Stunden.
                  </p>
                  <div>
                    <p className="font-semibold">E-Mail</p>
                    <p className="text-gray-600">info@homeyoursoul.de</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Häufige Fragen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {faqs.length > 0 ? (
                    faqs.map((f) => {
                      const isOpen = openId === f.id;
                      return (
                        <div
                          key={f.id}
                          className="border-b border-[#E8DCC8] last:border-b-0"
                          data-testid={`public-faq-${f.id}`}
                        >
                          <button
                            type="button"
                            onClick={() => setOpenId(isOpen ? null : f.id)}
                            className="w-full flex items-start justify-between gap-2 py-2 text-left group"
                          >
                            <p className="font-semibold text-sm text-[#5D4E3C] group-hover:text-[#C4A084] transition-colors">
                              {f.question}
                            </p>
                            <ChevronDown
                              className={`h-4 w-4 flex-shrink-0 text-[#8B7355] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            />
                          </button>
                          {isOpen && (
                            <p className="text-sm text-gray-600 pb-3 whitespace-pre-line">
                              {f.answer}
                            </p>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <>
                      <div>
                        <p className="font-semibold">Versand</p>
                        <p className="text-sm text-gray-600">
                          Wir versenden innerhalb von 1-3 Werktagen.
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold">Rückgabe</p>
                        <p className="text-sm text-gray-600">
                          14 Tage Rückgaberecht ab Erhalt der Ware.
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold">Zahlungsmethoden</p>
                        <p className="text-sm text-gray-600">
                          Wir akzeptieren PayPal, Kreditkarte und Vorkasse.
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;